import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

import swal from "sweetalert";
import {
  fetchFileDelete,
  uploadVoiceNoteservice,
} from "../../../services/BusinessFileUploadService";

const VoiceNote = ({
  open,
  setOpen,
  businessId,
  allFiles,
  getBusinessDetails,
}) => {
  const { t } = useTranslation();
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState("");
  const [title, setTitle] = useState("");
  const [voiceNotes, setVoiceNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);

  const user = JSON.parse(sessionStorage.getItem("userDetails"))?.result;
  console.log("user", user);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  const createNotePermission =
    user?.role?.id === 1 ||
    user?.role?.id === 2 ||
    (user?.role?.id === 3 && user?.loggedinprofiletype === "sellerprofile");
  const deleteNotePermission =
    user?.role?.id === 1 ||
    user?.role?.id === 2 ||
    (user?.role?.id === 3 && user?.loggedinprofiletype === "sellerprofile");

  useEffect(() => {
    if (open) {
      // fetchVoiceNotes();
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      const audios =
        allFiles
          ?.filter((f) => f.type === "audio")
          ?.map((file, idx) => ({
            id: file.id || idx,
            title: file.description || file.name || `Audio ${idx + 1}`,
            audioUrl: file.url,
            createdAt: file.createdat,
            duration: file.duration,
          })) || [];

      setVoiceNotes(audios);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [open, allFiles]);

  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/wav",
        });
        setRecordedBlob(audioBlob);
        setAudioUrl(URL.createObjectURL(audioBlob));
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);

      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("Error starting recording:", error);
      swal("Error", t("voiceNote_microphoneError"), "error");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(timerRef.current);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const uploadVoiceNote = async () => {
    if (!recordedBlob || !title.trim()) {
      swal("Error", t("voiceNote_titleAndAudioRequired"), "error");
      return;
    }

    const formData = new FormData();
    const audioFile = new File([recordedBlob], `${title.trim()}.wav`, {
      type: "audio/wav",
    });
    formData.append("file", audioFile);
    formData.append("entity", "BUSINESS");
    formData.append("entityid", Number(businessId));
    formData.append("description", title.trim());

    try {
      setIsLoading(true);
      setUploadProgress(0);

      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      const response = await uploadVoiceNoteservice(formData);
      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response) {
        resetForm();
        getBusinessDetails();
        swal("Success", t("voiceNote_uploadSuccess"), "success");
      } else {
        throw new Error("Upload failed");
      }
    } catch (error) {
      console.error("Error uploading voice note:", error);
      swal("Error", t("voiceNote_uploadError"), "error");
    } finally {
      setIsLoading(false);
      setUploadProgress(0);
    }
  };

  const resetForm = () => {
    setRecordedBlob(null);
    setAudioUrl("");
    setTitle("");
    setRecordingTime(0);
  };

  const deleteVoiceNote = async (id) => {
    swal({
      title: t("voiceNote_deleteConfirm"),
      icon: "warning",
      buttons: [t("common_close"), t("voiceNote_delete")],
      dangerMode: true,
    }).then(async (willDelete) => {
      if (!willDelete) return;

      try {
        const response = await fetchFileDelete(id);

        if (response) {
          setVoiceNotes((prev) => prev.filter((note) => note.id !== id));
          swal("Success", t("voiceNote_uploadSuccess"), "success");
          getBusinessDetails();
        } else {
          throw new Error("Delete failed");
        }
      } catch (error) {
        console.error("Error deleting voice note:", error);
        swal("Error", t("voiceNote_deleteError"), "error");
      }
    });
  };

  const handleClose = () => {
    resetForm();
    setOpen(false);
  };

  if (!open) return null;

  return (
    <>
      {/* Modal Backdrop */}
      <div
        className="modal-backdrop fade show"
        onClick={handleClose}
        style={{ zIndex: 1040 }}
      ></div>

      {/* Modal */}
      <div
        className="modal fade show d-block"
        tabIndex="-1"
        style={{ zIndex: 1050 }}
        onClick={handleClose}
      >
        <div
          className="modal-dialog modal-lg modal-dialog-centered"
          onClick={(e) => e.stopPropagation()}
          style={{
            maxWidth: "95vw",
            width: "800px",
            maxHeight: "95vh",
            margin: "2.5vh auto",
          }}
        >
          <div
            className="modal-content"
            style={{ height: "95vh", display: "flex", flexDirection: "column" }}
          >
            {/* Fixed Header */}
            <div
              className="modal-header bg-primary text-white flex-shrink-0"
              style={{ padding: "0.75rem 1rem" }}
            >
              <h5
                className="modal-title mb-0 gap-2 d-flex align-items-center"
                style={{ fontSize: "1.1rem" }}
              >
                <i className="fa fa-microphone mr-2"></i>
                {t("voiceNote_title")}
              </h5>
              <button
                type="button"
                className="modal-close text-white"
                onClick={handleClose}
                style={{
                  opacity: 1,
                  fontSize: "1.2rem",
                  border: "none",
                  background: "none",
                }}
              >
                <span>&times;</span>
              </button>
            </div>

            {/* Scrollable Body */}
            <div
              className="modal-body flex-fill"
              style={{
                padding: "1rem",
                overflowY: "auto",
                maxHeight: "calc(95vh - 120px)",
              }}
            >
              {/* Recording Section - Compact */}
              {createNotePermission && (
                <div className="mb-3">
                  <div className="card border-info">
                    <div className="card-header bg-light py-2">
                      <h6
                        className="mb-0 gap-2 d-flex align-items-center"
                        style={{ fontSize: "0.95rem" }}
                      >
                        <i className="fa fa-plus-circle mr-2 text-info"></i>
                        {t("voiceNote_createNew")}
                      </h6>
                    </div>
                    <div className="card-body py-3">
                      {/* Title Input */}
                      <div className="mb-3">
                        <label
                          htmlFor="title"
                          className="form-label font-weight-bold gap-2 d-flex align-items-center"
                          style={{ fontSize: "0.9rem" }}
                        >
                          <i className="fa fa-tag mr-1"></i>
                          {t("voiceNote_titleLabel")}
                        </label>
                        <input
                          type="text"
                          id="title"
                          className="form-control form-control-sm"
                          placeholder={t("voiceNote_titlePlaceholder")}
                          value={title}
                          onChange={(e) => setTitle(e.target.value)}
                          disabled={isLoading}
                        />
                      </div>

                      {/* Recording Controls - Compact */}
                      <div className="text-center mb-3">
                        <div className="recording-controls">
                          {!isRecording ? (
                            <button
                              onClick={startRecording}
                              className="btn btn-success btn-sm mr-2 "
                              disabled={isLoading}
                              style={{ minWidth: "120px" }}
                            >
                              <div className="gap-2 d-flex align-items-center">
                                <i className="fa fa-microphone mr-1"></i>
                                <small>{t("voiceNote_startRecording")}</small>
                              </div>
                            </button>
                          ) : (
                            <button
                              onClick={stopRecording}
                              className="btn btn-danger btn-sm mr-2"
                              style={{ minWidth: "120px" }}
                            >
                              <div className="gap-2 d-flex align-items-center">
                                <i className="fa fa-stop mr-1"></i>
                                <small>{t("voiceNote_stopRecording")}</small>
                              </div>
                            </button>
                          )}

                          {recordedBlob && (
                            <button
                              onClick={uploadVoiceNote}
                              className="btn btn-primary btn-sm"
                              style={{ minWidth: "120px", marginLeft: "8px" }}
                              disabled={isLoading || !title.trim()}
                            >
                              {isLoading ? (
                                <div classNames="gap-2 d-flex align-items-center">
                                  <i className="fa fa-spinner fa-spin mr-1"></i>
                                  <small>{t("voiceNote_uploading")}</small>
                                </div>
                              ) : (
                                <div classNames="gap-2 d-flex align-items-center">
                                  <i className="fa fa-upload mr-1"></i>
                                  <small className="ml-2">
                                    {t("voiceNote_uploadVoiceNote")}
                                  </small>
                                </div>
                              )}
                            </button>
                          )}
                        </div>

                        {/* Recording Status */}
                        {isRecording && (
                          <div className="mt-2">
                            <div className="alert alert-info py-2 mb-0">
                              <small className="gap-2 d-flex align-items-center">
                                <i
                                  className="fa fa-circle text-danger mr-1"
                                  style={{ animation: "blink 1s infinite" }}
                                ></i>
                                {t("voiceNote_recording")}{" "}
                                {formatTime(recordingTime)}
                              </small>
                            </div>
                          </div>
                        )}

                        {/* Upload Progress */}
                        {uploadProgress > 0 && uploadProgress < 100 && (
                          <div className="mt-2">
                            <div
                              className="progress"
                              style={{ height: "20px" }}
                            >
                              <div
                                className="progress-bar progress-bar-striped progress-bar-animated"
                                style={{ width: `${uploadProgress}%` }}
                              >
                                <small>{uploadProgress}%</small>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Audio Preview - Compact */}
                      {audioUrl && (
                        <div className="alert alert-success py-2">
                          <h6 className="mb-2" style={{ fontSize: "0.9rem" }}>
                            <i className="fa fa-volume-up mr-1"></i>
                            {t("voiceNote_previewTitle")}
                          </h6>
                          <audio
                            controls
                            className="w-100 mb-2"
                            style={{ height: "30px" }}
                          >
                            <source src={audioUrl} type="audio/wav" />
                            {t("voiceNote_audioNotSupported")}
                          </audio>
                          <div className="d-flex justify-content-between align-items-center">
                            <small className="text-muted">
                              {t("voiceNote_duration")}:{" "}
                              {formatTime(recordingTime)}
                            </small>
                            <button
                              onClick={resetForm}
                              className="btn btn-sm btn-outline-secondary"
                            >
                              <i className="fa fa-times mr-1"></i>
                              <small>{t("voiceNote_clear")}</small>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Voice Notes List - Scrollable */}
              <div>
                <div className="card border-secondary">
                  <div className="card-header bg-light py-2">
                    <h6
                      className="mb-0 gap-2 d-flex align-items-center"
                      style={{ fontSize: "0.95rem" }}
                    >
                      <i className="fa fa-list mr-2 text-secondary"></i>
                      {t("voiceNote_yourVoiceNotes")} ({voiceNotes.length})
                    </h6>
                  </div>
                  <div className="card-body p-2">
                    {isLoading && voiceNotes.length === 0 ? (
                      <div className="text-center py-3 ">
                        <i className="fa fa-spinner fa-spin fa-lg text-muted"></i>
                        <p className="mt-2 mb-0 text-muted">
                          <small>{t("voiceNote_loading")}</small>
                        </p>
                      </div>
                    ) : voiceNotes.length === 0 ? (
                      <div className="text-center py-4 ">
                        <i className="fa fa-microphone fa-2x text-muted mb-2"></i>
                        <h6 className="text-muted mb-1">
                          {t("voiceNote_noVoiceNotes")}
                        </h6>
                        <p className="text-muted mb-0">
                          <small>{t("voiceNote_createFirstNote")}</small>
                        </p>
                      </div>
                    ) : (
                      <div
                        className="voice-notes-list"
                        style={{
                          maxHeight: "300px",
                          overflowY: "auto",
                          paddingRight: "5px",
                        }}
                      >
                        {voiceNotes.map((note, index) => (
                          <div key={note.id || index} className="mb-2">
                            <div
                              className="card border-left-primary"
                              style={{ borderLeftWidth: "3px" }}
                            >
                              <div className="card-body py-2 px-3">
                                <div className="d-flex justify-content-between align-items-start mb-1">
                                  <h6
                                    className="card-title mb-1 gap-2 d-flex align-items-center"
                                    style={{
                                      fontSize: "0.9rem",
                                      lineHeight: "1.2",
                                    }}
                                  >
                                    <i
                                      className="fa fa-music mr-1 text-primary"
                                      style={{ fontSize: "0.8rem" }}
                                    ></i>
                                    {note.title}
                                  </h6>
                                  {deleteNotePermission && (
                                    <button
                                      onClick={() => deleteVoiceNote(note.id)}
                                      className="btn btn-sm btn-outline-danger"
                                      title={t("voiceNote_delete")}
                                      style={{
                                        fontSize: "0.75rem",
                                        padding: "2px 6px",
                                      }}
                                    >
                                      <i className="fa fa-trash"></i>
                                    </button>
                                  )}
                                </div>

                                <div className="mb-2  ">
                                  <span
                                    className="text-muted gap-2 d-flex align-items-center"
                                    style={{ fontSize: "0.75rem" }}
                                  >
                                    <i className="fa fa-calendar mr-1"></i>
                                    {note.createdAt
                                      ? new Date(
                                          note.createdAt
                                        ).toLocaleDateString()
                                      : t("voiceNote_today")}
                                    {note.duration && (
                                      <>
                                        <i className="fa fa-clock-o ml-2 mr-1"></i>
                                        {note.duration}
                                      </>
                                    )}
                                  </span>
                                </div>

                                <audio
                                  controls
                                  className="w-100"
                                  style={{ height: "30px" }}
                                >
                                  <source
                                    src={note.audioUrl || "#"}
                                    type="audio/wav"
                                  />
                                  {t("voiceNote_audioNotSupported")}
                                </audio>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Fixed Footer */}
            <div
              className="modal-footer flex-shrink-0"
              style={{ padding: "0.75rem 1rem" }}
            >
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={handleClose}
              >
                <i className="fa fa-times mr-1"></i>
                {t("common_close")}
              </button>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes blink {
          0%,
          50% {
            opacity: 1;
          }
          51%,
          100% {
            opacity: 0.3;
          }
        }

        .border-left-primary {
          border-left: 3px solid #007bff !important;
        }

        .voice-notes-list {
          scrollbar-width: thin;
          scrollbar-color: #dee2e6 transparent;
        }

        .voice-notes-list::-webkit-scrollbar {
          width: 6px;
        }

        .voice-notes-list::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }

        .voice-notes-list::-webkit-scrollbar-thumb {
          background: #dee2e6;
          border-radius: 3px;
        }

        .voice-notes-list::-webkit-scrollbar-thumb:hover {
          background: #adb5bd;
        }

        .modal-dialog {
          display: flex;
          align-items: center;
        }

        .recording-controls .btn {
          font-size: 0.875rem;
        }

        audio::-webkit-media-controls-panel {
          background-color: #f8f9fa;
        }

        .modal-close {
          font-weight: 300;
          line-height: 1;
        }

        @media (max-width: 768px) {
          .modal-dialog {
            maxwidth: 95vw !important;
            width: 95vw !important;
            margin: 1vh auto;
          }

          .modal-content {
            height: 98vh !important;
          }

          .modal-body {
            maxheight: calc(98vh - 110px) !important;
          }

          .recording-controls .btn {
            minwidth: 100px !important;
            fontsize: 0.8rem;
          }

          .voice-notes-list {
            maxheight: 250px !important;
          }
        }

        @media (max-width: 576px) {
          .recording-controls {
            display: flex;
            flex-direction: column;
            gap: 8px;
          }

          .recording-controls .btn {
            width: 100%;
            margin: 0 !important;
          }
        }
      `}</style>
    </>
  );
};

export default VoiceNote;
